import BatteryPower from "components/atomics/BatteryPower";
import DetailInfo, { ArrayValue } from "components/atomics/DetailInfo";
import LoadingSpinner from "components/atomics/LoadingSpinner";
import { assetDeviceApplicationState, lhbDeviceState } from "components/pages/AssetManagement/utils";
import { GetDeviceForDeviceDetailsQuery } from "graphqlBase/Devices/__generated__/getDeviceForDeviceDetails";
import React from "react";
import translations from "translations";
import { formatTimeDistance } from "translations/formatter";

interface TrackerProps {
  deviceDetailsData: GetDeviceForDeviceDetailsQuery | undefined;
  lastTimeConnection: {
    key: string;
    value: string;
  };
  loading: boolean;
}

const { battery, Temperature, Humidity, appState } = translations.entities.capability.capabilityNames;
const TrackerLargeSmall: React.FC<TrackerProps> = ({ lastTimeConnection, deviceDetailsData, loading }) => {
  const deviceFirmware = deviceDetailsData?.device?.mappingDeviceFirmwareDevice.map(
    (e) => e.deviceFirmware.firmwareVersion
  );

  const getCurrentValue = (capabilityIdentifier: string, numberToFixed?: number) => {
    return deviceDetailsData?.device?.sensorMeasurementCurrentValues?.items
      ?.find((val) => val?.capability?.capabilityName.includes(capabilityIdentifier))
      ?.valueFloat?.toFixed(numberToFixed);
  };

  const getCurrentState = () => {
    const currentValue = parseInt(getCurrentValue("State") ?? "") ?? 0;

    const hasAsset = (deviceDetailsData?.device?.assetDevices?.items?.length ?? 0) > 0;

    const isCrane =
      hasAsset &&
      deviceDetailsData?.device?.assetDevices?.items?.[0]?.asset.assetSource?.assetSourceName.includes("CRANE");

    const assetState = isCrane ? assetDeviceApplicationState[currentValue] : lhbDeviceState[currentValue];

    return !hasAsset ? "" : `${assetState?.label ?? ""}${assetState?.extendedLabel ?? ""}`;
  };

  const batteryVoltage = deviceDetailsData?.device?.sensorMeasurementCurrentValues?.items?.find(
    (item) => item?.capabilityId === "80bec84b-2c50-4e12-9a70-56b06e5b92e5"
  )?.valueFloat;

  const batteryChargeStatus = deviceDetailsData?.device?.sensorMeasurementCurrentValues?.items?.find(
    (item) => item?.capabilityId === "3c9e8f2a-1b4d-4e6f-9a8c-7d5b2e4a1c9f"
  )?.valueFloat;

  const trackerInfo: ArrayValue[] = [
    {
      label: translations.entities.deviceType.columns.deviceTypeName,
      value: deviceDetailsData?.device?.deviceType.deviceTypeName ?? "",
    },
    {
      label: "Firmware",
      value: deviceFirmware?.length ? deviceFirmware[0] : "",
    },
    {
      label: battery,
      value: <BatteryPower batteryChargeStatus={batteryChargeStatus} batteryVoltage={batteryVoltage?.toString()} />,
    },
    {
      label: Temperature,
      value: getCurrentValue("Temperature", 1) ? `${getCurrentValue("Temperature", 1)} °C` : "",
    },
    {
      label: Humidity,
      value: getCurrentValue("Humidity", 1) ? `${getCurrentValue("Humidity", 1)} %` : "",
    },
    {
      label: appState,
      value: getCurrentState(),
    },
    {
      label: "Last Connection",
      value: lastTimeConnection ? formatTimeDistance(new Date(lastTimeConnection.value)) : "",
    },
  ];

  return (
    <LoadingSpinner sx={{ height: "fit-content" }} loading={loading}>
      <DetailInfo data={trackerInfo} itemHeight="30px" />
    </LoadingSpinner>
  );
};

export default TrackerLargeSmall;
